import { listOrganizations } from '@np-forge/api-client/organizations'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/context/AuthContext'
import { logger } from '@/lib/logger'
import { ROUTES } from '@/routes/paths'
import { persistedRedirectUrl } from '@/services/redirectState.service'

/**
 * Component that ensures the user has an organization and redirects to the library.
 * Used as a catch-all for invalid routes to prevent 404s.
 */
export function EnsureOrganization() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const { user, loading: authLoading } = useAuth()

  useEffect(() => {
    const redirectToLibrary = async () => {
      // Only attempt organization requests when authenticated
      if (authLoading || !user) {
        setLoading(false)
        return
      }

      // Check if there's a saved redirect URL (from 401 handling)
      const savedUrl = persistedRedirectUrl.get()
      if (savedUrl) {
        // Clear the saved URL
        persistedRedirectUrl.clear()
        // Redirect back to where user was
        void navigate(savedUrl, { replace: true })
        setLoading(false)
        return
      }

      try {
        const orgs = await listOrganizations()
        const firstOrgId = orgs.organizations?.[0]?.id

        if (firstOrgId) {
          // Redirect to workspace library
          void navigate(ROUTES.WORKSPACE_LIBRARY.buildPath({ id: firstOrgId }), { replace: true })
        } else {
          // No workspace: the public guard at "/" decides (terms, saved link, a default workspace or the B2B portal)
          void navigate(ROUTES.ROOT.fullPath, { replace: true })
        }
      } catch (error) {
        logger.error('Failed to fetch workspaces:', error)
        // On error, redirect to terms
        void navigate(ROUTES.ACCEPT_TERMS_OF_SERVICE.fullPath, { replace: true })
      } finally {
        setLoading(false)
      }
    }

    void redirectToLibrary()
  }, [navigate, user, authLoading])

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          fontSize: '16px',
          color: '#555',
        }}
      >
        Loading...
      </div>
    )
  }

  return null
}
